import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUpRight,
  CreditCard,
  History,
  Layers3,
  Wallet,
  X,
} from 'lucide-react';
import { SubscriptionPlan, SubscriptionDetails } from '../../types/subscription';
import { subscriptionApi } from '../../services/subscription.service';
import api from '../../services/api';
import PricingPlans from './PricingPlansView';
import SubscriptionDetailsComponent from './SubscriptionDetails';
import PaymentHistory from './PaymentHistory';

type ViewType = 'plans' | 'current' | 'payments' | 'wallet';

interface SubscriptionManagerProps {
  onClose?: () => void;
}

interface TeacherSelectionApi {
  _id?: string | { $oid?: string };
  id?: string;
  teacherId: string;
  teacherName?: string;
  selectedAt?: string;
}

interface TeacherOption {
  teacherId: string;
  teacherName: string;
  selectedAt?: string;
}

const viewOptions: Array<{
  id: ViewType;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  { id: 'plans', label: 'Plans', description: 'Explore pricing and upgrades', icon: Layers3 },
  { id: 'current', label: 'Current', description: 'Manage active subscription', icon: CreditCard },
  { id: 'payments', label: 'History', description: 'Review previous payments', icon: History },
  { id: 'wallet', label: 'Wallet', description: 'Top up and pay teachers', icon: Wallet },
];

export const SubscriptionManager: React.FC<SubscriptionManagerProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const [view, setView] = useState<ViewType>('plans');
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [topupAmount, setTopupAmount] = useState('');
  const [teacherId, setTeacherId] = useState('');
  const [teacherOptions, setTeacherOptions] = useState<TeacherOption[]>([]);
  const [payAmount, setPayAmount] = useState('');
  const hasCurrentSubscription = Boolean(subscriptionDetails?.currentSubscription?.id);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const [plansResult, subscriptionResult] = await Promise.allSettled([
        subscriptionApi.getAllPlans('STUDENT'),
        subscriptionApi.getCurrentSubscription(),
      ]);

      if (plansResult.status === 'fulfilled') {
        setPlans(plansResult.value);
      } else {
        throw plansResult.reason;
      }

      if (subscriptionResult.status === 'fulfilled') {
        setSubscriptionDetails(subscriptionResult.value);
      } else {
        const statusCode = (subscriptionResult.reason as any)?.response?.status;
        if (statusCode === 404) {
          setSubscriptionDetails(null);
        } else {
          throw subscriptionResult.reason;
        }
      }

      setError(null);
    } catch (err) {
      setError('Failed to load subscription data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadInitialData();
  }, [loadInitialData]);

  const handleRefresh = () => {
    void loadInitialData();
  };

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      void loadInitialData();
    }, 30000);
    return () => window.clearInterval(intervalId);
  }, [loadInitialData]);

  const loadWalletBalance = async () => {
    try {
      const response = await api.get<{ balance: number }>('/teachers/wallet/balance');
      setWalletBalance(Number(response.data?.balance || 0));
    } catch (err) {
      console.error('Failed to load wallet balance:', err);
    }
  };

  const loadSelectedTeachers = useCallback(async () => {
    try {
      const response = await api.get<TeacherSelectionApi[]>('/teachers/selections/student');
      const selections = Array.isArray(response.data) ? response.data : [];
      if (!selections.length) return;

      const sortedSelections = [...selections].sort((a, b) => {
        const aTime = new Date(a.selectedAt || 0).getTime();
        const bTime = new Date(b.selectedAt || 0).getTime();
        return bTime - aTime;
      });

      const uniqueOptions = sortedSelections.reduce<TeacherOption[]>((acc, selection) => {
        const nextTeacherId = String(selection.teacherId || '').trim();
        if (!nextTeacherId || acc.some((item) => item.teacherId === nextTeacherId)) {
          return acc;
        }

        acc.push({
          teacherId: nextTeacherId,
          teacherName: String(selection.teacherName || 'Selected Teacher').trim() || 'Selected Teacher',
          selectedAt: selection.selectedAt,
        });
        return acc;
      }, []);

      setTeacherOptions(uniqueOptions);
      if (!teacherId && uniqueOptions[0]?.teacherId) {
        setTeacherId(uniqueOptions[0].teacherId);
      }
    } catch (err) {
      console.error('Failed to load selected teachers:', err);
    }
  }, [teacherId]);

  useEffect(() => {
    if (view === 'wallet') {
      void loadWalletBalance();
      if (!teacherOptions.length) {
        void loadSelectedTeachers();
      }
    }
  }, [view, teacherOptions.length, loadSelectedTeachers]);

  const selectedTeacher = teacherOptions.find((option) => option.teacherId === teacherId) || null;

  const handleTopupNavigate = () => {
    const amount = Number(topupAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      alert('Enter a valid top-up amount.');
      return;
    }
    navigate('/student/wallet/topup', { state: { amount } });
  };

  const handlePayTeacher = async () => {
    const amount = Number(payAmount);
    if (!teacherId.trim()) {
      alert('Select a teacher.');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      alert('Enter a valid payment amount.');
      return;
    }

    try {
      const response = await api.post<{ qrCodeUrl?: string; balance?: number }>(
        '/teachers/wallet/pay',
        {
          teacherId: teacherId.trim(),
          amount,
          note: 'Subscription panel payment',
        },
      );
      if (response.data?.qrCodeUrl) {
        window.open(response.data.qrCodeUrl, '_blank', 'noopener,noreferrer');
      }
      setPayAmount('');
      if (typeof response.data?.balance === 'number') {
        setWalletBalance(response.data.balance);
      }
      alert(`Payment sent to ${selectedTeacher?.teacherName || 'teacher'} successfully.`);
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Payment failed.';
      alert(message);
    }
  };

  const summaryCards = useMemo(
    () => [
      {
        label: 'Available Plans',
        value: String(plans.length),
        tone: 'bg-sky-50 text-sky-900 border-sky-200',
      },
      {
        label: 'Current Status',
        value: hasCurrentSubscription ? 'Active' : 'None',
        tone: hasCurrentSubscription
          ? 'bg-emerald-50 text-emerald-900 border-emerald-200'
          : 'bg-amber-50 text-amber-900 border-amber-200',
      },
      {
        label: 'Wallet Balance',
        value: `INR ${walletBalance.toFixed(2)}`,
        tone: 'bg-slate-50 text-slate-900 border-slate-200',
      },
    ],
    [hasCurrentSubscription, plans.length, walletBalance],
  );

  return (
    <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_30px_90px_-48px_rgba(15,23,42,0.35)]">
      <div className="border-b border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.16),_transparent_22%),linear-gradient(135deg,_#020617_0%,_#0f172a_48%,_#1d4ed8_100%)] px-6 py-7 text-white sm:px-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-sky-100">Subscription Manager</p>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              Control plans, renewals, payments, and wallet movement from one place.
            </h1>
            <p className="mt-3 text-sm leading-6 text-slate-200 sm:text-base">
              Move between pricing, current subscription state, payment records, and teacher wallet actions without leaving the workflow.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => navigate('/subscriptions')}
              className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/10 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/15"
            >
              Open Full Page
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </button>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
              >
                <X className="mr-2 h-4 w-4" />
                Close
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          {summaryCards.map((card) => (
            <div key={card.label} className={`rounded-[1.5rem] border px-4 py-4 ${card.tone}`}>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] opacity-70">{card.label}</p>
              <p className="mt-2 text-2xl font-semibold tracking-tight">{card.value}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-0 lg:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="border-b border-slate-200 bg-slate-50/80 p-4 lg:border-b-0 lg:border-r">
          <div className="grid gap-3">
            {viewOptions.map((option) => {
              const Icon = option.icon;
              const active = view === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setView(option.id)}
                  className={`rounded-[1.35rem] border p-4 text-left transition ${
                    active
                      ? 'border-slate-950 bg-slate-950 text-white shadow-[0_20px_60px_-40px_rgba(15,23,42,0.7)]'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <Icon className={`h-5 w-5 ${active ? 'text-sky-200' : 'text-slate-500'}`} />
                    <span className={`text-xs font-semibold uppercase tracking-[0.18em] ${active ? 'text-sky-200' : 'text-slate-400'}`}>
                      {option.label}
                    </span>
                  </div>
                  <p className={`mt-5 text-base font-semibold ${active ? 'text-white' : 'text-slate-950'}`}>{option.label}</p>
                  <p className={`mt-1 text-sm ${active ? 'text-slate-300' : 'text-slate-500'}`}>{option.description}</p>
                </button>
              );
            })}
          </div>
        </aside>

        <main className="min-w-0 bg-white p-5 sm:p-6 lg:p-8">
          {error && (
            <div className="mb-6 rounded-[1.5rem] border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex min-h-[360px] items-center justify-center rounded-[1.75rem] border border-slate-200 bg-slate-50">
              <div className="text-center">
                <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-slate-950" />
                <p className="mt-4 text-sm font-medium text-slate-600">Loading subscription workspace...</p>
              </div>
            </div>
          ) : (
            <>
              {view === 'plans' && (
                <div className="space-y-6">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Pricing</p>
                    <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">Available plans</h2>
                    <p className="mt-1 text-sm text-slate-500">Compare plan capacity and move into the right subscription tier.</p>
                  </div>
                  <PricingPlans
                    plans={plans}
                    currentSubscription={subscriptionDetails?.currentSubscription}
                    onSubscriptionChange={handleRefresh}
                  />
                </div>
              )}

              {view === 'current' && hasCurrentSubscription && subscriptionDetails && (
                <div className="space-y-6">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Current plan</p>
                    <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">Subscription status and usage</h2>
                    <p className="mt-1 text-sm text-slate-500">Review renewal timing, feature usage, and cancellation controls.</p>
                  </div>
                  <SubscriptionDetailsComponent details={subscriptionDetails} onUpdate={handleRefresh} />
                </div>
              )}

              {view === 'current' && !hasCurrentSubscription && (
                <div className="rounded-[1.75rem] border border-slate-200 bg-slate-50 p-8">
                  <h3 className="text-2xl font-semibold tracking-tight text-slate-950">No active subscription</h3>
                  <p className="mt-2 max-w-xl text-sm text-slate-500">
                    There is no active plan attached to this account yet. Browse available plans and activate one when ready.
                  </p>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => setView('plans')}
                      className="rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                    >
                      View Plans
                    </button>
                    <button
                      type="button"
                      onClick={handleRefresh}
                      className="rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      Refresh
                    </button>
                  </div>
                </div>
              )}

              {view === 'payments' && (
                <div className="space-y-6">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Payments</p>
                    <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">Payment history</h2>
                    <p className="mt-1 text-sm text-slate-500">Track successful transactions, failures, and invoices in one timeline.</p>
                  </div>
                  <PaymentHistory onRefresh={handleRefresh} />
                </div>
              )}

              {view === 'wallet' && (
                <div className="space-y-6">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Wallet & UPI</p>
                    <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">Fund wallet and settle teacher payments</h2>
                    <p className="mt-1 text-sm text-slate-500">
                      Add funds, then complete wallet-based teacher payouts using the backend-controlled payout details.
                    </p>
                  </div>

                  <div className="grid gap-6 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
                    <div className="space-y-6">
                      <div className="rounded-[1.75rem] border border-slate-200 bg-slate-950 p-6 text-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.65)]">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Available balance</p>
                        <p className="mt-3 text-4xl font-semibold tracking-tight">INR {walletBalance.toFixed(2)}</p>
                        <p className="mt-2 text-sm text-slate-300">
                          Refresh the wallet after top-ups or teacher payouts to keep the balance snapshot current.
                        </p>
                        <button
                          type="button"
                          onClick={loadWalletBalance}
                          className="mt-5 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/10"
                        >
                          Refresh Balance
                        </button>
                      </div>

                      <div className="rounded-[1.75rem] border border-slate-200 bg-slate-50 p-6">
                        <h3 className="text-lg font-semibold text-slate-950">Add amount via UPI</h3>
                        <p className="mt-1 text-sm text-slate-500">Top up first, then continue with teacher or subscription payments from your wallet.</p>
                        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                          <input
                            type="number"
                            min="1"
                            step="0.01"
                            value={topupAmount}
                            onChange={(e) => setTopupAmount(e.target.value)}
                            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-slate-500 sm:max-w-[220px]"
                            placeholder="Amount"
                          />
                          <button
                            type="button"
                            onClick={handleTopupNavigate}
                            className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                          >
                            Top-up Wallet
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-[1.75rem] border border-emerald-200 bg-[linear-gradient(180deg,_#f7fffb_0%,_#ffffff_100%)] p-6 shadow-[0_20px_60px_-45px_rgba(16,185,129,0.35)]">
                      <h3 className="text-lg font-semibold text-slate-950">Pay teacher via wallet + UPI</h3>
                      <p className="mt-1 text-sm text-slate-500">
                        Choose a teacher by name. The backend fetches that teacher&apos;s payout details at payment time.
                      </p>

                      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1.3fr)_170px]">
                        <select
                          value={teacherId}
                          onChange={(e) => setTeacherId(e.target.value)}
                          className="rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500"
                        >
                          <option value="">Select teacher</option>
                          {teacherOptions.map((option) => (
                            <option key={option.teacherId} value={option.teacherId}>
                              {option.teacherName}
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          min="1"
                          step="0.01"
                          value={payAmount}
                          onChange={(e) => setPayAmount(e.target.value)}
                          className="rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-emerald-500"
                          placeholder="Amount"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={handlePayTeacher}
                        className="mt-4 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700"
                      >
                        Pay Teacher
                      </button>

                      {selectedTeacher && (
                        <div className="mt-5 rounded-[1.5rem] border border-emerald-100 bg-emerald-50 px-4 py-4">
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Selected teacher</p>
                          <p className="mt-2 text-base font-semibold text-slate-950">{selectedTeacher.teacherName}</p>
                        </div>
                      )}

                      {!teacherOptions.length && (
                        <div className="mt-5 rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                          No selected teachers found yet. Select a teacher first, then return here to pay.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default SubscriptionManager;
